// EventSource wrapper with typed events + automatic reconnect.
// Backend contract: docs/design-docs/transcript-stitching.md (§ SSE).

import type { Meeting, MeetingEvent, Participant, Segment, SummaryRow } from "./types";

export interface MeetingEvents {
  segment: (s: Segment) => void;
  chunk: (e: { seq: number; billed_seconds: number }) => void;
  status: (m: Partial<Meeting> & { state: string }) => void;
  summary: (s: SummaryRow) => void;
  providerError: (e: { seq?: number; message: string }) => void;
  importProgress: (e: { done: number; total: number }) => void;
  participant: (e: { event: string; participant: Participant }) => void;
  meetingEvent: (e: MeetingEvent) => void;
}

export class MeetingStream {
  private es: EventSource | null = null;

  constructor(
    private meetingId: number,
    private handlers: Partial<MeetingEvents>,
    /// Guest token for hosted meetings (D14).
    private gt?: string | null,
  ) {}

  start(): void {
    // EventSource reconnects itself and sends Last-Event-ID; the
    // backend replays missed segments from SQL on reconnect.
    const suffix = this.gt ? `?gt=${encodeURIComponent(this.gt)}` : "";
    this.es = new EventSource(`/api/meetings/${this.meetingId}/events${suffix}`);
    const on = <T>(name: string, fn?: (v: T) => void) => {
      if (!fn) return;
      this.es!.addEventListener(name, (ev) => {
        try {
          fn(JSON.parse((ev as MessageEvent).data) as T);
        } catch {
          /* malformed event — skip */
        }
      });
    };
    on("segment", this.handlers.segment);
    on("chunk", this.handlers.chunk);
    on("status", this.handlers.status);
    on("summary", this.handlers.summary);
    on("provider-error", this.handlers.providerError);
    on("import-progress", this.handlers.importProgress);
    on("participant", this.handlers.participant);
    on("meeting-event", this.handlers.meetingEvent);
    this.es.addEventListener("lagged", () => {
      // Server says we fell behind: force a clean reconnect.
      this.stop();
      this.start();
    });
  }

  stop(): void {
    this.es?.close();
    this.es = null;
  }
}
